import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';

import { useEventCallback } from '../../../common/utils/hooks';
import { SET_PICKLIST_ITEMS_TUB_WEIGHT_EXCLUDED_QUANTITY } from '../../../common/graphql/pickListItem';

import { getPickedQuantity, toNonNegativeNumber } from './PickListDetailHelpers';

// Picked quantities left out of the tub weight, per product: the exclusions
// dialog, its quantity editor and the dock's "Exclude last" shortcut.
const usePickListTubWeightExclusions = ({
	mappedPickInventory,
	setMappedPickInventory,
	canEditPickList,
	lastPickedProductAction,
}) => {
	const [setTubWeightExcludedQuantityMutation, { loading: loadingTubWeightExclusions }] = useMutation(SET_PICKLIST_ITEMS_TUB_WEIGHT_EXCLUDED_QUANTITY);
	const [isTubWeightExclusionsDialogOpen, setIsTubWeightExclusionsDialogOpen] = useState(false);
	const [tubWeightExclusionEditProductId, setTubWeightExclusionEditProductId] = useState(null);
	const [tubWeightExclusionEditQuantity, setTubWeightExclusionEditQuantity] = useState(0);

	const tubWeightProductExclusionSummaries = useMemo(() => {
		const summaryByProductId = mappedPickInventory.reduce((acc, item) => {
			const productId = item?.product?.id;
			if (!productId || item?.product?.excludeFromTubWeightCalculation) {
				return acc;
			}

			if (!acc[productId]) {
				acc[productId] = {
					productId,
					productName: item?.product?.name || 'Unknown product',
					product: item?.product,
					itemIds: [],
					pickedQuantity: 0,
					excludedQuantity: 0,
				};
			}

			const pickedQuantity = getPickedQuantity(item);
			const excludedQuantity = Math.min(
				toNonNegativeNumber(item?.tubWeightExcludedQuantity),
				pickedQuantity,
			);

			acc[productId].itemIds.push(item.id);
			acc[productId].pickedQuantity += pickedQuantity;
			acc[productId].excludedQuantity += excludedQuantity;

			return acc;
		}, {});

		return Object.values(summaryByProductId)
			.map(summary => ({
				...summary,
				pickedQuantity: toNonNegativeNumber(summary.pickedQuantity),
				excludedQuantity: Math.min(toNonNegativeNumber(summary.excludedQuantity), toNonNegativeNumber(summary.pickedQuantity)),
			}))
			.filter(summary => summary.pickedQuantity > 0 || summary.excludedQuantity > 0)
			.sort((summaryA, summaryB) => {
				if (summaryB.excludedQuantity !== summaryA.excludedQuantity) {
					return summaryB.excludedQuantity - summaryA.excludedQuantity;
				}
				return (summaryA.productName || '').localeCompare(summaryB.productName || '');
			});
	}, [mappedPickInventory]);

	const tubWeightExcludedSummary = useMemo(() => {
		return tubWeightProductExclusionSummaries.reduce((acc, summary) => {
			acc.excludedQuantity += toNonNegativeNumber(summary.excludedQuantity);
			if (summary.excludedQuantity > 0) {
				acc.excludedProductsCount += 1;
			}
			return acc;
		}, {
			excludedQuantity: 0,
			excludedProductsCount: 0,
		});
	}, [tubWeightProductExclusionSummaries]);

	const tubWeightExclusionEditSummary = useMemo(() => {
		return tubWeightProductExclusionSummaries.find(summary => summary.productId === tubWeightExclusionEditProductId) || null;
	}, [tubWeightExclusionEditProductId, tubWeightProductExclusionSummaries]);

	const setTubWeightExcludedQuantityForProduct = useEventCallback(async ({
		productId,
		excludedQuantity,
	}) => {
		const targetSummary = tubWeightProductExclusionSummaries.find(summary => summary.productId === productId);
		if (!targetSummary || !canEditPickList) {
			return;
		}

		const maxExcludableQuantity = toNonNegativeNumber(targetSummary.pickedQuantity);
		const nextExcludedQuantity = Math.min(
			toNonNegativeNumber(excludedQuantity),
			maxExcludableQuantity,
		);

		const response = await setTubWeightExcludedQuantityMutation({
			variables: {
				ids: targetSummary.itemIds,
				excludedQuantity: nextExcludedQuantity,
			},
		});

		const updatedItems = response?.data?.setPickListItemsTubWeightExcludedQuantity || [];
		if (!updatedItems.length) {
			return;
		}

		const excludedQuantityByItemId = updatedItems.reduce((acc, updatedItem) => {
			acc[updatedItem.id] = toNonNegativeNumber(updatedItem.tubWeightExcludedQuantity);
			return acc;
		}, {});

		setMappedPickInventory(prevItems => prevItems.map(item => {
			if (excludedQuantityByItemId[item.id] === undefined) {
				return item;
			}
			return {
				...item,
				tubWeightExcludedQuantity: excludedQuantityByItemId[item.id],
			};
		}));
	});

	const openTubWeightExclusionEditor = useCallback((productId, prefillQuantity) => {
		const targetSummary = tubWeightProductExclusionSummaries.find(summary => summary.productId === productId);
		if (!targetSummary) {
			return;
		}

		const defaultQuantity = prefillQuantity === undefined
			? targetSummary.excludedQuantity
			: prefillQuantity;

		setTubWeightExclusionEditProductId(productId);
		setTubWeightExclusionEditQuantity(Math.min(
			toNonNegativeNumber(defaultQuantity),
			toNonNegativeNumber(targetSummary.pickedQuantity),
		));
	}, [tubWeightProductExclusionSummaries]);

	const closeTubWeightExclusionEditor = useCallback(() => {
		setTubWeightExclusionEditProductId(null);
		setTubWeightExclusionEditQuantity(0);
	}, []);

	const applyTubWeightExclusionEdit = useEventCallback(async () => {
		if (!tubWeightExclusionEditSummary) {
			closeTubWeightExclusionEditor();
			return;
		}

		await setTubWeightExcludedQuantityForProduct({
			productId: tubWeightExclusionEditSummary.productId,
			excludedQuantity: tubWeightExclusionEditQuantity,
		});
		closeTubWeightExclusionEditor();
	});

	const applyQuickExcludeLastPicked = useEventCallback(async () => {
		if (!lastPickedProductAction?.productId || !lastPickedProductAction?.quantity) {
			return;
		}

		const targetSummary = tubWeightProductExclusionSummaries.find(summary => summary.productId === lastPickedProductAction.productId);
		if (!targetSummary) {
			return;
		}

		await setTubWeightExcludedQuantityForProduct({
			productId: targetSummary.productId,
			excludedQuantity: targetSummary.excludedQuantity + lastPickedProductAction.quantity,
		});
	});

	const lastPickedProductSummary = useMemo(() => {
		if (!lastPickedProductAction?.productId) {
			return null;
		}
		return tubWeightProductExclusionSummaries.find(summary => summary.productId === lastPickedProductAction.productId) || null;
	}, [lastPickedProductAction, tubWeightProductExclusionSummaries]);

	const openLastPickedTubWeightEditor = useCallback(() => {
		if (!lastPickedProductSummary) {
			return;
		}
		openTubWeightExclusionEditor(
			lastPickedProductSummary.productId,
			lastPickedProductSummary.excludedQuantity + toNonNegativeNumber(lastPickedProductAction?.quantity),
		);
	}, [lastPickedProductAction?.quantity, lastPickedProductSummary, openTubWeightExclusionEditor]);

	return {
		loadingTubWeightExclusions,
		isTubWeightExclusionsDialogOpen,
		setIsTubWeightExclusionsDialogOpen,
		tubWeightExclusionEditQuantity,
		setTubWeightExclusionEditQuantity,
		tubWeightProductExclusionSummaries,
		tubWeightExcludedSummary,
		tubWeightExclusionEditSummary,
		setTubWeightExcludedQuantityForProduct,
		openTubWeightExclusionEditor,
		closeTubWeightExclusionEditor,
		applyTubWeightExclusionEdit,
		applyQuickExcludeLastPicked,
		lastPickedProductSummary,
		openLastPickedTubWeightEditor,
	};
};

export default usePickListTubWeightExclusions;
